'use client';

import { If } from '@payments-checkout/shared/components';
import { HTTP_STATUS_HELP_TAG_TEXT } from '@payments-checkout/shared/helpers/constants/http-status.const';
import { analiticaInstance } from '@payments-checkout/shared/plugins/analitica/analitica.events';
import { resolveProjectFromHost } from '@payments-checkout/shared/plugins/analitica/analitica.helpers';
import { Box, Button, Text } from '@stone-payments/jade';
import { useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useTranslationProvider } from '../../app/providers/translationProvider';
import {
  Content,
  ErrorCodeTag,
  FlexBox,
  ImageContainer,
  LinearBackgroundContainer,
  TextContainer,
  Title,
} from './error.styles';
import { ErrorIllustration } from './illustration/error-illustration';

type ErrorProps = {
  tryAgainButton: boolean;
  code: string;
};

export function Error({ tryAgainButton, code }: ErrorProps) {
  const { replace } = useRouter();
  const { formatMessage } = useIntl();
  const { currentLocale } = useTranslationProvider();
  const pageViewedRef = useRef(false);

  useEffect(() => {
    if (pageViewedRef.current) return;

    pageViewedRef.current = true;
    analiticaInstance.events.errorPage.loadErrorViewed(
      code,
      currentLocale === 'en' ? 'ingles' : 'portugues',
      resolveProjectFromHost(window.location.hostname)
    );
  }, [code, currentLocale]);

  function errorMessage(code: string, message: string) {
    return 'error' + code + message;
  }

  const hasTag = HTTP_STATUS_HELP_TAG_TEXT.includes(Number(code));

  function refreshWithoutErrorParams() {
    const currentPath = window.location.pathname;
    const newPath = currentPath.replace(`/error/${code}`, '');

    replace(newPath)
  }

  return (
    <FlexBox>
      <ImageContainer>
        <ErrorIllustration />
      </ImageContainer>
      <LinearBackgroundContainer>
        <Content>
          <If
            condition={hasTag}
            render={() => (
              <ErrorCodeTag size="small" variant="neutral" solid>
                {formatMessage({ id: errorMessage(code, '.tag') })}
              </ErrorCodeTag>
            )}
          />
          <TextContainer>
            <Box>
              <Title
                title={formatMessage({ id: errorMessage(code, '.title') })}
              />
              <Text color="medium" variant="text-medium" weight="regular">
                <FormattedMessage id={errorMessage(code, '.subtitle')} />
              </Text>
            </Box>
          </TextContainer>
        </Content>
        <If condition={tryAgainButton && code !== '404'}>
          <Button onClick={refreshWithoutErrorParams} icon="action-refresh" size="medium">
            <FormattedMessage id="error.button" />
          </Button>
        </If>
      </LinearBackgroundContainer>
    </FlexBox>
  );
}
